import React from 'react';
import { X, Package, Tag, DollarSign, Box, MapPin, Calendar, CheckCircle2, XCircle, Edit3, Image as ImageIcon } from 'lucide-react';

interface CityPricingItem {
  id?: number;
  city_id?: number;
  city_name?: string;
  cityName?: string;
  zone_id?: number;
  zone_name?: string;
  zoneName?: string;
  mrp?: number;
  price?: number;
  base_price?: number;
  tax_amount?: number;
  crate_price?: number;
  effective_from?: string;
  effectiveFrom?: string;
  effective_to?: string;
  effectiveTo?: string;
  status?: number | string;
}

interface CrateInfo {
  id?: number;
  name?: string;
  units?: number;
}

export interface ViewProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: any; // Product details object
  onEdit?: () => void;
  isLoading?: boolean;
}

const renderTextLabel = (val: any, fallback: string = '-'): string => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    return String(
      val.name ||
      val.brand_name ||
      val.brandName ||
      val.category_name ||
      val.title ||
      val.sizeValue ||
      val.variantName ||
      val.username ||
      fallback
    );
  }
  return fallback;
};

export const ViewProductModal: React.FC<ViewProductModalProps> = ({
  isOpen,
  onClose,
  product,
  onEdit,
  isLoading = false,
}) => {
  const [previewImage, setPreviewImage] = React.useState<string | null>(null);

  if (!isOpen) return null;

  // Helper to recursively unwrap nested API response structures
  const unwrapProductData = (obj: any): any => {
    if (!obj || typeof obj !== 'object') return {};
    let current = obj;
    while (current && typeof current === 'object' && !Array.isArray(current)) {
      if (current.data && typeof current.data === 'object' && !Array.isArray(current.data) && (current.data.id || current.data.product || current.data.data)) {
        current = current.data;
      } else if (current.product && typeof current.product === 'object' && !Array.isArray(current.product)) {
        current = current.product;
      } else {
        break;
      }
    }
    return current || {};
  };

  // Extract combined product details (handling nested API wrappers & direct objects)
  const p = unwrapProductData(product);

  const name = renderTextLabel(p.name || p.displayName, 'N/A');
  const id = renderTextLabel(p.id, '-');
  const skuCode = renderTextLabel(p.sku_code || p.sapCode || p.code, '-');

  // Category & HSN Code extraction
  const categoryObj = (typeof p.category === 'object' && p.category !== null) ? p.category : null;
  const categoryName = renderTextLabel(p.category_name || categoryObj?.name || p.category, '-');

  const rawHsn =
    categoryObj?.hsnCode ??
    categoryObj?.hsn_code ??
    categoryObj?.hsn ??
    p.hsn_code ??
    p.hsnCode ??
    p.hsn ??
    p.hsn_no ??
    p.hsnNo ??
    p.hsn_number ??
    p.hsnNumber ??
    p.hsn_sac_code ??
    p.hsnSacCode ??
    p.tax_rate?.hsnCode ??
    p.tax_rate?.hsn_code ??
    p.taxRate?.hsnCode ??
    p.taxRate?.hsn_code ??
    (typeof p.tax_rate === 'string' || typeof p.tax_rate === 'number' ? p.tax_rate : null) ??
    (typeof p.taxRate === 'string' || typeof p.taxRate === 'number' ? p.taxRate : null);

  const hsnCode = renderTextLabel(rawHsn, '-');

  // Brand Name
  const brandObj = (typeof p.brand === 'object' && p.brand !== null) ? p.brand : null;
  const brand = renderTextLabel(brandObj?.name || p.brand, '-');

  // Size
  const sizeObj = (typeof p.size === 'object' && p.size !== null) ? p.size : null;
  const sizeVal = sizeObj
    ? `${sizeObj.sizeValue || sizeObj.name || ''} ${sizeObj.unit?.name || sizeObj.unit?.symbol || ''}`.trim()
    : renderTextLabel(p.size, '-');
  const size = sizeVal || '-';

  const isActive = p.is_active === 1 || p.is_active === true || p.status === 'Active' || p.status === 1 || p.status === '1';

  // Financials
  const mrp = typeof p.mrp === 'number' ? p.mrp : Number(p.mrp || 0);
  const price = typeof p.price === 'number' ? p.price : Number(p.price || 0);
  const basePrice = typeof p.base_price === 'number' ? p.base_price : (typeof p.basePrice === 'number' ? p.basePrice : Number(p.base_price || p.basePrice || 0));
  const taxAmount = typeof p.tax_amount === 'number' ? p.tax_amount : (typeof p.taxAmount === 'number' ? p.taxAmount : Number(p.tax_amount || p.taxAmount || 0));
  const gstPercent = typeof p.gst_percent === 'number' ? p.gst_percent : (typeof p.gstPercentage === 'number' ? p.gstPercentage : Number(p.gst_percent || p.gstPercentage || p.gst || 0));
  const cratePrice = typeof p.crate_price === 'number' ? p.crate_price : (typeof p.cratePrice === 'number' ? p.cratePrice : Number(p.crate_price || p.cratePrice || 0));
  const unitsPerCase = p.units_per_case || p.unitsPerCase || p.unitsPerCrate || (p.crate?.units) || 0;

  // Crate details
  const crateObj: CrateInfo | null = (typeof p.crate === 'object' && p.crate !== null) ? p.crate : null;
  const crateName = renderTextLabel(crateObj?.name || p.crate_name || p.crateName, '-');
  const crateUnits = crateObj?.units || unitsPerCase || '-';

  // Regional City Pricings
  const cityPricings: CityPricingItem[] = (Array.isArray(p.city_pricings) && p.city_pricings.length > 0)
    ? p.city_pricings
    : (Array.isArray(p.pricing) && p.pricing.length > 0)
    ? p.pricing
    : [];

  // Media & Gallery Image Extraction
  const mediaObj = Array.isArray(p.media) && p.media.length > 0
    ? p.media[0]
    : (typeof p.media === 'object' && p.media !== null ? p.media : null);

  const logoImg: string | null =
    mediaObj?.logoImg ||
    p.logoImg ||
    p['main-img'] ||
    null;

  const rawSlider =
    mediaObj?.sliderImg ||
    p.sliderImg ||
    p['slider-img'];

  const sliderImgs: string[] = Array.isArray(rawSlider)
    ? rawSlider.map((img: any) => (typeof img === 'string' ? img : img.url || '')).filter(Boolean)
    : [];

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto animate-fadeIn select-none">
      <div className="bg-white rounded-3xl max-w-4xl w-full my-8 shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-[#FFF8E1] px-6 py-5 border-b border-amber-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl go_erp-bg text-white flex items-center justify-center font-bold text-lg shadow-sm">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-extrabold text-slate-800 tracking-tight">{name}</h2>
                <span
                  className={`px-3 py-0.5 text-[11px] font-bold rounded-full ${
                    isActive
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                      : 'bg-rose-100 text-rose-700 border border-rose-200'
                  }`}
                >
                  {isActive ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                SAP Code: <span className="font-semibold text-slate-700">{skuCode}</span> &bull; ID:{' '}
                <span className="font-semibold text-slate-700">{id}</span>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors flex items-center justify-center cursor-pointer border border-slate-200"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {isLoading ? (
            <div className="py-12 text-center text-xs font-semibold text-slate-500">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#C1272D] border-t-transparent mb-3"></div>
              <p>Loading product specifications...</p>
            </div>
          ) : (
            <>
              {/* Section 1: Basic Specifications */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                  <Tag className="w-4 h-4 text-[#C1272D]" />
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    General Specifications
                  </h4>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-slate-50/70 p-4 rounded-2xl border border-slate-100">
                  <div>
                    <span className="text-slate-400 text-xs font-medium block">Category</span>
                    <span className="text-xs font-bold text-slate-800">{categoryName}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs font-medium block">Brand</span>
                    <span className="text-xs font-bold text-slate-800">{brand}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs font-medium block">Size</span>
                    <span className="text-xs font-bold text-slate-800">{size}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs font-medium block">HSN Code</span>
                    <span className="text-xs font-bold text-slate-800">{hsnCode}</span>
                  </div>
                </div>
              </div>

              {/* Section 2: Pricing & Tax Details */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                  <DollarSign className="w-4 h-4 text-[#C1272D]" />
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Pricing & Tax Information
                  </h4>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 bg-amber-50/40 p-4 rounded-2xl border border-amber-100/70">
                  <div>
                    <span className="text-amber-800/70 text-xs font-medium block">MRP</span>
                    <span className="text-sm font-extrabold text-slate-900">₹{mrp.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-amber-800/70 text-xs font-medium block">Selling Price</span>
                    <span className="text-sm font-extrabold text-slate-900">₹{price.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-amber-800/70 text-xs font-medium block">Base Price</span>
                    <span className="text-sm font-bold text-slate-800">₹{basePrice.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-amber-800/70 text-xs font-medium block">GST Percent</span>
                    <span className="text-sm font-bold text-slate-800">{gstPercent}%</span>
                  </div>
                  <div>
                    <span className="text-amber-800/70 text-xs font-medium block">Tax Amount</span>
                    <span className="text-sm font-bold text-slate-800">₹{taxAmount.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-amber-800/70 text-xs font-medium block">Crate Price</span>
                    <span className="text-sm font-extrabold text-emerald-700">₹{cratePrice.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-amber-800/70 text-xs font-medium block">Units / Crate</span>
                    <span className="text-sm font-bold text-slate-800">{unitsPerCase} units</span>
                  </div>
                </div>
              </div>

              {/* Section 3: Crate Details */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                  <Box className="w-4 h-4 text-[#C1272D]" />
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Crate Specifications
                  </h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-slate-50/70 p-4 rounded-2xl border border-slate-100">
                  <div>
                    <span className="text-slate-400 text-xs font-medium block">Crate Name</span>
                    <span className="text-xs font-bold text-slate-800">{crateName}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs font-medium block">Crate ID</span>
                    <span className="text-xs font-bold text-slate-800">{crateObj?.id || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs font-medium block">Crate Capacity</span>
                    <span className="text-xs font-bold text-slate-800">{crateUnits} Units</span>
                  </div>
                </div>
              </div>

              {/* Section 4: City & Regional Pricings */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                  <MapPin className="w-4 h-4 text-[#C1272D]" />
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    City & Regional Pricing Rules ({cityPricings.length})
                  </h4>
                </div>

                {cityPricings.length === 0 ? (
                  <div className="p-4 text-center text-xs font-medium text-slate-400 bg-slate-50 rounded-2xl border border-slate-100">
                    No custom city pricing rules assigned for this product.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-2xl border border-slate-200">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px] tracking-wider">
                          <th className="py-2.5 px-4">City</th>
                          <th className="py-2.5 px-4">Zone</th>
                          <th className="py-2.5 px-4">MRP</th>
                          <th className="py-2.5 px-4">Price</th>
                          <th className="py-2.5 px-4">Crate Price</th>
                          <th className="py-2.5 px-4">Effective Dates</th>
                          <th className="py-2.5 px-4">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-slate-800">
                        {cityPricings.map((cp, idx) => (
                          <tr key={cp.id || idx} className="hover:bg-slate-50 transition-colors">
                            <td className="py-3 px-4 font-bold text-slate-800">{cp.city_name || cp.cityName || '-'}</td>
                            <td className="py-3 px-4 font-medium text-slate-600">{cp.zone_name || cp.zoneName || '-'}</td>
                            <td className="py-3 px-4 font-semibold text-slate-800">₹{(cp.mrp || 0).toFixed(2)}</td>
                            <td className="py-3 px-4 font-bold text-slate-900">₹{(cp.price || 0).toFixed(2)}</td>
                            <td className="py-3 px-4 font-extrabold text-emerald-700">
                              ₹{(cp.crate_price || 0).toFixed(2)}
                            </td>
                            <td className="py-3 px-4 font-medium text-slate-500 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-slate-400" />
                                {formatDate(cp.effective_from || cp.effectiveFrom)} - {formatDate(cp.effective_to || cp.effectiveTo)}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full inline-flex items-center gap-1 ${
                                  cp.status === 1 || cp.status === '1' || cp.status === 'ACTIVE'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-rose-100 text-rose-700'
                                }`}
                              >
                                {cp.status === 1 || cp.status === '1' || cp.status === 'ACTIVE' ? (
                                  <>
                                    <CheckCircle2 className="w-3 h-3" /> Active
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-3 h-3" /> Inactive
                                  </>
                                )}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Section 5: Product Media & Gallery */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                  <ImageIcon className="w-4 h-4 text-[#C1272D]" />
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Product Media & Gallery
                  </h4>
                </div>

                {!logoImg && sliderImgs.length === 0 ? (
                  <div className="p-4 text-center text-xs font-medium text-slate-400 bg-slate-50 rounded-2xl border border-slate-100">
                    No product images or gallery media available.
                  </div>
                ) : (
                  <div className="bg-slate-50/70 p-4 rounded-2xl border border-slate-100 space-y-4">
                    {/* Primary SKU / Logo Image */}
                    {logoImg && (
                      <div>
                        <span className="text-slate-400 text-xs font-medium block mb-2">Main SKU Logo Image</span>
                        <div
                          onClick={() => setPreviewImage(logoImg)}
                          className="w-24 h-24 rounded-xl border border-slate-200 bg-white p-1 overflow-hidden shadow-xs cursor-zoom-in hover:border-[#C1272D] hover:shadow-md transition-all group relative block"
                          title="Click to view full image"
                        >
                          <img
                            src={logoImg}
                            alt={name}
                            className="w-full h-full object-contain rounded-lg group-hover:scale-105 transition-transform"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Slider Images Gallery */}
                    {sliderImgs.length > 0 && (
                      <div>
                        <span className="text-slate-400 text-xs font-medium block mb-2">
                          Slider Gallery Images ({sliderImgs.length})
                        </span>
                        <div className="flex flex-wrap gap-3">
                          {sliderImgs.map((imgUrl, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => setPreviewImage(imgUrl)}
                              className="w-20 h-20 rounded-xl border border-slate-200 bg-white p-1 overflow-hidden shadow-xs hover:border-[#C1272D] hover:shadow-md transition-all group block relative cursor-zoom-in"
                              title={`Click to view image ${index + 1}`}
                            >
                              <img
                                src={imgUrl}
                                alt={`${name} slider ${index + 1}`}
                                className="w-full h-full object-contain rounded-lg group-hover:scale-105 transition-transform"
                                onError={(e) => {
                                  (e.target as HTMLElement).style.display = 'none';
                                }}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-slate-300 rounded-xl text-xs font-semibold text-slate-600 hover:bg-white transition-colors cursor-pointer"
          >
            Close
          </button>

          {onEdit && (
            <button
              onClick={() => {
                onClose();
                onEdit();
              }}
              className="px-5 py-2 go_erp-bg text-white text-xs font-bold rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
            >
              <Edit3 className="w-4 h-4" />
              <span>Edit Product</span>
            </button>
          )}
        </div>
      </div>

      {/* Full Image Pop-up Preview Lightbox */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-slate-900/80 backdrop-blur-md animate-fadeIn"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-2xl w-full max-h-[85vh] bg-transparent rounded-3xl p-3 sm:p-5 pt-10 sm:pt-12 shadow-2xl flex flex-col items-center justify-center transition-all duration-300 transform scale-100 animate-scaleUp border border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-2.5 right-2.5 sm:top-3.5 sm:right-3.5 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center cursor-pointer shadow-xs z-20"
              title="Close image preview"
            >
              <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
            <div className="w-full h-full flex items-center justify-center overflow-hidden">
              <img
                src={previewImage}
                alt="Expanded Preview"
                className="max-w-full max-h-[65vh] sm:max-h-[72vh] object-contain rounded-2xl transition-transform duration-300 shadow-sm"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewProductModal;
